import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from './HamburgerMenu';
import ProfileIcon from './ProfileIcon';
import Stopwatch from './Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { canAccessBuilders, BUILDER_ACCESS_DENIED_MESSAGE } from '../utils/builderAccess';
import { normalizeStudentUserRecord, isUserRecord } from '../utils/sessionUser';

export default function BuilderAccessGate({
  navigation,
  route,
  title = 'Builders',
  children,
  canAccess = canAccessBuilders,
  deniedMessage = BUILDER_ACCESS_DENIED_MESSAGE,
}) {
  const theme = useTheme();
  const stopwatchRef = React.useRef(null);
  const styles = getStyles(theme);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [loading, setLoading] = useState(!route?.params?.user);

  useEffect(() => {
    const userId = route?.params?.user?.id ?? route?.params?.userId;
    if (userId == null) {
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${getApiUrl()}/api/users/${userId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (isUserRecord(data)) {
          setCurrentUser(normalizeStudentUserRecord(data));
        } else {
          setCurrentUser(null);
        }
      })
      .catch(() => setCurrentUser(null))
      .finally(() => setLoading(false));
  }, [route?.params?.user?.id, route?.params?.userId]);

  if (loading) {
    return (
      <View style={styles.container}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={theme.colors.primary || '#d4af37'} />
        </View>
      </View>
    );
  }

  if (!canAccess(currentUser)) {
    return (
      <View style={styles.container}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <View style={styles.header}>
          <View style={styles.hamburgerContainer}>
            <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} currentUser={currentUser} />
          </View>
          <View style={styles.breadcrumbContainer}>
            <Text style={styles.breadcrumb}>{title}</Text>
          </View>
          <View style={styles.profileIconContainer}>
            <ProfileIcon navigation={navigation} user={currentUser} />
          </View>
        </View>
        <View style={styles.deniedWrap}>
          <Text style={styles.deniedText}>{deniedMessage}</Text>
        </View>
        <Stopwatch ref={stopwatchRef} />
      </View>
    );
  }

  return children;
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    loadingWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    deniedWrap: {
      flex: 1,
      paddingHorizontal: 24,
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    deniedText: {
      fontSize: 16,
      lineHeight: 24,
      textAlign: 'center',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
  });
